type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogParams {
    message: string;
    error?: unknown;
    context?: Record<string, unknown>;
    requestId?: string;
    userId?: string;
    path?: string;
    method?: string;
}

interface NextJsError extends Error {
    digest?: string;
    code?: string;
}

const REDACTED_VALUE = '[REDACTED]';
const MAX_LOG_DEPTH = 5;
const SENSITIVE_KEY_PATTERN =
    /email|phone|mobile|tel|firstName|lastName|fullName|address|postalCode|zip|consumer|customer|contact|privatePerson|billing|shipping/i;
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

const sanitizeString = (value: string) => {
    return value.replace(EMAIL_PATTERN, REDACTED_VALUE);
};

const sanitizeValue = (value: unknown, depth = 0): unknown => {
    if (depth >= MAX_LOG_DEPTH) {
        return '[Truncated]';
    }

    if (typeof value === 'string') {
        return sanitizeString(value);
    }

    if (Array.isArray(value)) {
        return value.map(item => sanitizeValue(item, depth + 1));
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    if (value instanceof Error) {
        return {
            name: value.name,
            message: sanitizeString(value.message),
            stack: value.stack ? sanitizeString(value.stack) : undefined,
        };
    }

    return Object.fromEntries(
        Object.entries(value).map(([key, nestedValue]) => {
            if (SENSITIVE_KEY_PATTERN.test(key)) {
                return [key, REDACTED_VALUE];
            }

            return [key, sanitizeValue(nestedValue, depth + 1)];
        }),
    );
};

class Logger {
    private isServer = typeof window === 'undefined';
    private env = process.env.NODE_ENV || 'development';

    constructor() {
        if (this.isServer) {
            process.on('uncaughtException', error => {
                this.error('Uncaught Exception', { error });
                // Don't exit the process in production as it would restart the container
                if (this.env !== 'production') {
                    process.exit(1);
                }
            });

            process.on('unhandledRejection', reason => {
                this.error('Unhandled Rejection', {
                    error: reason,
                });
            });
        }
    }

    debug(message: string, params?: Omit<LogParams, 'message'>) {
        this.log('debug', { message, ...params });
    }

    info(message: string, params?: Omit<LogParams, 'message'>) {
        this.log('info', { message, ...params });
    }

    warn(message: string, params?: Omit<LogParams, 'message'>) {
        this.log('warn', { message, ...params });
    }

    error(message: string, params?: Omit<LogParams, 'message'>) {
        this.log('error', { message, ...params });
    }

    private log(level: LogLevel, { message, error, context, requestId, userId, path }: LogParams) {
        const timestamp = new Date().toISOString();
        const sanitizedError = error ? sanitizeValue(error) : undefined;
        const sanitizedContext = context ? sanitizeValue(context) : undefined;
        const errorDetails =
            error && sanitizedError && typeof sanitizedError === 'object'
                ? {
                      error: 'message' in sanitizedError ? sanitizedError.message : undefined,
                      stack:
                          this.env === 'production'
                              ? undefined
                              : 'stack' in sanitizedError
                                ? sanitizedError.stack
                                : undefined,
                      digest: error instanceof Error ? (error as NextJsError)?.digest : undefined,
                      code: error instanceof Error ? (error as NextJsError)?.code : undefined,
                  }
                : {};
        const traceDetails =
            this.isServer && process.env._X_AMZN_TRACE_ID
                ? {
                      traceId: process.env._X_AMZN_TRACE_ID,
                  }
                : {};

        const logEntry = {
            level,
            timestamp,
            message: sanitizeString(message),
            requestId,
            userId: userId ? REDACTED_VALUE : undefined,
            path,
            context: sanitizedContext,
            ...errorDetails,
            ...traceDetails,
        };

        // In a browser, don't output as JSON
        if (!this.isServer) {
            if (level === 'error') {
                console.error(sanitizeString(message), sanitizedError || '', sanitizedContext || '');
            } else if (level === 'warn') {
                console.warn(sanitizeString(message), sanitizedError || '', sanitizedContext || '');
            } else {
                console.info(sanitizeString(message), sanitizedError || '', sanitizedContext || '');
            }
            return;
        }

        // On the server, output structured JSON logs for CloudWatch
        if (level === 'error') {
            console.error(JSON.stringify(logEntry));
        } else {
            process.stdout.write(`${JSON.stringify(logEntry)}\n`);
        }
    }
}

export const logger = new Logger();
